import { useState, useEffect } from "react";
import { Checkbox, Select } from "@lainco/react-toolbox";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight, faCode } from "@fortawesome/free-solid-svg-icons";
import { get, cloneDeep } from "lodash";

function Expander({ value, onChange }) {
  return (
    <FontAwesomeIcon
      icon={value ? faChevronDown : faChevronRight}
      style={{ cursor: "pointer", marginRight: "0.5em" }}
      onClick={() => onChange(!value)}
    ></FontAwesomeIcon>
  );
}

function Variable({ label, value, options, onChange }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "1em", alignItems: "center", padding: "0.25em 0" }}>
      <span>
        <FontAwesomeIcon icon={faCode} style={{ marginRight: "0.5em" }} />
        {label}
      </span>
      <div style={{ zoom: "0.8" }}>
        <Select value={value} onChange={onChange} options={options} />
      </div>
    </div>
  );
}

const toOptions = (options) => options.map((option) => (typeof option === "object" ? option : { id: option, name: option }));

function Scenario({
  scenario,
  onScenarioChange,
  onExpandedChange,
  onOptionChange,
  variablesByName,
  onVariableChange,
  level = 0,
  path = [],
}) {
  const { variables, ..._scenario } = scenario;
  return (
    <>
      {variables && (
        <div style={{ paddingLeft: level * 20 + "px", paddingTop: "0.25em" }}>
          {variables.map(({ id, name, default: defaultValue, options }) => (
            <Variable
              key={name}
              label={name}
              value={variablesByName[id]}
              options={toOptions(options)}
              onChange={(v) => onVariableChange?.(id, v)}
            />
          ))}
        </div>
      )}
      {Object.entries(_scenario)
        .filter(([key, _]) => key !== "variables")
        .map(([key, value]) => (
          <div key={key}>
            {value.path == null ? (
              <>
                <div style={{ paddingLeft: level * 20 + "px", paddingTop: "0.5em" }}>
                  <Expander value={value.expanded} onChange={(v) => onExpandedChange?.([...path, key], v)} />
                  {key}
                </div>
                {value.expanded && (
                  <Scenario
                    scenario={value.items}
                    onScenarioChange={onScenarioChange}
                    onExpandedChange={onExpandedChange}
                    onOptionChange={onOptionChange}
                    variablesByName={variablesByName}
                    onVariableChange={onVariableChange}
                    level={level + 1}
                    path={[...path, key]}
                  />
                )}
              </>
            ) : (
              <div
                style={{
                  alignItems: "center",
                  paddingLeft: level * 20 + "px",
                  paddingBottom: "0.25em",
                  paddingTop: "0.25em",
                  display: "grid",
                  gridTemplateColumns: `1fr repeat(${value.options?.length || 0}, auto)`,
                  gap: "1em",
                }}
              >
                <Checkbox checked={value.isSelected} text={key} onChange={() => onScenarioChange?.([...path, key], !value.isSelected)} />
                {value.options && (
                  <>
                    {value.options.map((option, i) => (
                      <div style={{ zoom: "0.8" }}>
                        <Select value={option.value} onChange={(v) => onOptionChange?.([...path, key], i, v)} options={option.options} />
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        ))}
    </>
  );
}

const isLeaf = (object) => object.path != null;

const transpose = (obj, metadata, level) => {
  const { _variables, ...object } = obj;
  const result = { variables: _variables };
  if (isLeaf(object)) {
    result.path = object.path;
    result.value = object.value;
    result.isSelected = object?.isSelected ?? false;
    result.level = level;
    result.options = object?.options
      ? object?.options.map((option) => ({
          ...option,
          value: option?.value ?? option?.default,
          options: option.options.map((x) => (typeof x === "object" ? x : { id: x, name: x })),
        }))
      : undefined;
  } else {
    Object.entries(object).forEach(([key, value]) => {
      if (isLeaf(value)) {
        result[key] = transpose(value, metadata?.[key] || null, level + 1);
      } else {
        result[key] = { expanded: metadata?.[key]?.expanded ?? true, items: transpose(value, metadata?.[key]?.items || null, level + 1) };
      }
    });
  }
  return result;
};

export function ScenariosTab({ scenarios, onScenarioChange, onOptionChange, variablesByName, setVariable }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(transpose(scenarios, data, 1));
  }, [scenarios]);

  const expandedChange = (path, value) => {
    const _data = cloneDeep(data);
    const _path = path.join(".items.").split(".");
    const node = get(_data, _path);
    node.expanded = value;
    const res = transpose(scenarios, _data, 1);
    setData(res);
  };

  if (data == null) return <div style={{ padding: "1em", height: "calc(100vh - 7.7em)" }}>Loading...</div>;
  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: "0", padding: "0.5em 1em", height: "calc(100vh - 7.7em)", overflowY: "auto" }}
    >
      <Scenario
        scenario={data}
        onScenarioChange={onScenarioChange}
        onExpandedChange={expandedChange}
        onOptionChange={onOptionChange}
        variablesByName={variablesByName}
        onVariableChange={setVariable}
      />
    </div>
  );
}
